/**
 * The payment step: Stripe's Payment Element on the studio's own account
 * (wallets where the browser offers them, a saved card asking for its security
 * code), Payments secured by Stripe, and Confirm & pay. Once paid, the booked page.
 * Its states are the app's payment page's (shared-client-screens D4, usePaymentFlow):
 * deposits not taken yet, card payments paused, too close to the start to pay,
 * already paid earlier, confirming, and a confirmation that takes a while.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'payment-step',
  description: "The checkout's payment: Stripe's card form on the studio's account, wallets where offered, and Confirm & pay.",
  drawing: 'standard-only',
  options: {},
  parts: {
    card: { description: "Stripe's payment form: wallets, a saved card, a new card.", required: true },
    secure: { description: 'Payments secured by Stripe.', required: false },
    notice: { description: 'Why there is nothing to pay with, or nothing more to do: deposits, card paused, too close to pay, already paid.', required: true },
    decline: { description: 'Why the card was declined, above the button.', required: true },
    pay: { description: 'Confirm & pay with the amount; waits while the checkout asks for the number to call.', required: true, states: ['disabled'], role: 'primary-action' },
    pickNewTime: { description: 'Pick a new time, when the held time is too close to its start to pay.', required: true, role: 'primary-action' },
    confirming: { description: 'Shown while the payment is confirmed, and while the confirmation takes a while.', required: true },
    done: { description: 'Done, while the confirmation takes a while: the studio home.', required: true, role: 'primary-action' },
  },
  actions: {
    pay: { description: 'Confirms the payment with what is filled in.', args: [] },
    pickNewTime: { description: 'Goes back a step to pick a new time.', args: [] },
    done: { description: 'Leaves a confirmation that takes a while for the studio home; the booking still arrives.', args: [] },
  },
  events: {
    paid: 'The payment went through and the booking is confirmed.',
  },
});

/** What the payment step knows. */
export interface PaymentStepState {
  /** The amount drawn on the button: "$120.00". */
  readonly amount: string;
  /** True once the card form is filled in enough to pay, and false again once the held time has run out on the client's clock. */
  readonly complete: boolean;
  /** form (filling in), confirming (paid, waiting for the booking), slowConfirm (still confirming after ten seconds), lapsed (too close to the start to pay: Pick a new time), or alreadyPaid (an earlier payment already went through, nothing more to do). */
  readonly phase: 'form' | 'confirming' | 'lapsed' | 'slowConfirm' | 'alreadyPaid';
  /** True while a payment is being sent. */
  readonly paying: boolean;
  /** True while the checkout asks for the number to call (service-by-phone D5): Confirm & pay waits for it. */
  readonly waitingForNumber: boolean;
  /** The decline in the card's words; null when none. */
  readonly decline: string | null;
  /** The way out after a decline ("No charge was made. Try a different card."); null when none. */
  readonly declineNote: string | null;
  /** cardOnly (the card form), or why there is no way to pay: depositBlocked, hardBlock (card payments paused). */
  readonly view: 'cardOnly' | 'depositBlocked' | 'hardBlock';
  /** The state's notice in the app's words; null when the card form shows on its own. */
  readonly notice: { readonly variant: 'info' | 'warning'; readonly title: string; readonly body: string } | null;
  /** The studio's name, as the notices name it. */
  readonly businessName: string;
}

/** What a drawing can ask the payment step to do. */
export interface PaymentStepActions {
  /** Confirm the payment with what is filled in. */
  readonly pay: () => void;
  /** Go back a step to pick a new time. */
  readonly pickNewTime: () => void;
  /** Leave a slow confirmation for the studio home. */
  readonly done: () => void;
}

/** What the payment step tells its host. */
export interface PaymentStepEvents {
  /** The payment went through; the booking it confirmed. */
  readonly paid: { readonly bookingId: string };
}
